import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio, BellRing, CalendarDays, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const FANS_IMG = "https://images.unsplash.com/photo-1762013315117-1c8005ad2b41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxleGNpdGVkJTIwc3BvcnRzJTIwZmFucyUyMHN0YWRpdW18ZW58MHx8fHwxNzg4MTk0OTkzfDA&ixlib=rb-4.1.0&q=85";

function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      if (u.pathname.startsWith("/embed/")) return url;
      if (u.pathname.startsWith("/live/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
    }
    if (u.hostname === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    return url;
  } catch {
    return url;
  }
}

function oversToBalls(o) {
  const [ov, b] = String(o || "0").split(".");
  return (parseInt(ov) || 0) * 6 + (parseInt(b) || 0);
}

function winChance(match) {
  const inn = match.innings || [];
  if (!inn.length || !inn[0].label) return null;
  const first = inn[0];
  const second = inn[1] && inn[1].label ? inn[1] : null;
  if (second) {
    const target = first.runs + 1;
    const needed = target - second.runs;
    const ballsLeft = 120 - oversToBalls(second.overs);
    if (needed <= 0) return { team: second.label, pct: 99 };
    if (ballsLeft <= 0) return { team: first.label, pct: 99 };
    const rrr = (needed / ballsLeft) * 6;
    const played = oversToBalls(second.overs);
    const crr = played > 0 ? (second.runs / played) * 6 : 0;
    let prob = 50 + (crr - rrr) * 9 + (10 - second.wickets - 5) * 3.5;
    prob = Math.max(2, Math.min(98, prob));
    return { team: second.label, pct: Math.round(prob) };
  }
  const b = oversToBalls(first.overs);
  if (!b) return { team: first.label, pct: 50 };
  const proj = (first.runs / b) * 120;
  let prob = 50 + (proj - 160) * 0.8 + (10 - first.wickets - 5) * 2;
  prob = Math.max(2, Math.min(98, prob));
  return { team: first.label, pct: Math.round(prob) };
}

function WinMeter({ match }) {
  const w = winChance(match);
  if (!w) return null;
  const other = w.team === match.team_a ? match.team_b : match.team_a;
  const opp = 100 - w.pct;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.3, ease: EASE }}
      className="mt-8 max-w-3xl backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6"
      data-testid="win-chance-meter"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">Win chance</span>
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">Live projection — moves with every ball</span>
      </div>
      <div className="flex h-3.5 rounded-full overflow-hidden bg-white/10">
        <motion.div
          className="h-full bg-white"
          initial={false}
          animate={{ width: `${w.pct}%` }}
          transition={{ duration: 0.8, ease: EASE }}
          data-testid="win-chance-bar-fav"
        />
        <motion.div
          className="h-full bg-league-red"
          initial={false}
          animate={{ width: `${opp}%` }}
          transition={{ duration: 0.8, ease: EASE }}
          data-testid="win-chance-bar-other"
        />
      </div>
      <div className="mt-3 flex items-center justify-between font-oswald">
        <span className="text-white font-semibold text-lg" data-testid="win-chance-fav">{w.team} — {w.pct}%</span>
        <span className="text-league-red font-semibold text-lg" data-testid="win-chance-other">{opp}% — {other}</span>
      </div>
    </motion.div>
  );
}

function ballClasses(b) {
  if (b === "W") return "bg-league-red text-white";
  if (b === "4") return "bg-league-blue text-white";
  if (b === "6") return "bg-white text-league-blue";
  if (b === "WD" || b === "NB") return "bg-white/25 text-white";
  return "bg-white/10 text-white";
}

function NotifyCard({ source }) {
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    try {
      const { data } = await api.post("/subscribe", { email: f.email.value, source });
      toast.success(data.message);
      f.reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that right now.");
    }
  };
  return (
    <div className="max-w-lg backdrop-blur-xl bg-white/70 saturate-150 border border-white rounded-2xl p-6 sm:p-8" data-testid={`notify-card-${source}`}>
      <div className="flex items-center gap-2 text-league-blue mb-2">
        <CalendarDays size={16} />
        <span className="text-[11px] font-bold uppercase tracking-[0.25em]">19–21 Feb 2027 · Umra Cricket Ground</span>
      </div>
      <p className="text-league-ink/70 text-sm mb-5">Drop your email and we'll notify you the moment the stream goes live.</p>
      <form onSubmit={submit} className="flex gap-3">
        <input
          type="email"
          name="email"
          required
          placeholder="you@example.com"
          data-testid={`notify-email-${source}`}
          className="flex-1 min-w-0 px-4 py-3 rounded-full border border-league-blue/30 bg-white text-sm text-league-ink placeholder:text-league-ink/40 focus:outline-none focus:ring-2 focus:ring-league-blue"
        />
        <button
          type="submit"
          data-testid={`notify-submit-${source}`}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-league-blue text-white text-sm font-bold uppercase tracking-wider transition-colors duration-200 hover:bg-league-red"
        >
          <BellRing size={15} /> Notify me
        </button>
      </form>
    </div>
  );
}

export default function Live() {
  const [match, setMatch] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const fetchMatch = () =>
    api.get("/live/match")
      .then((r) => { setMatch(r.data); setRefreshedAt(new Date()); })
      .catch(() => {})
      .finally(() => setLoaded(true));

  useEffect(() => {
    fetchMatch();
    const t = setInterval(fetchMatch, 8000);
    return () => clearInterval(t);
  }, []);

  const hasMatch = match && match.exists !== false;
  const isLive = hasMatch && match.active;

  return (
    <main className="relative min-h-screen overflow-hidden bg-league-deep" data-testid="live-page">
      <img src={FANS_IMG} alt="Stadium crowd" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-league-deep/80 via-league-blue/40 to-league-deep" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 w-full">
        {isLive ? (
          <div data-testid="live-scoreboard">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex flex-wrap items-center gap-4 mb-8">
              <span className="bg-transparent"><img src="/asl-logo-white.png" alt="All Star League crest" className="h-10 w-auto drop-shadow-lg" /></span>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-league-red text-white text-[11px] font-bold uppercase tracking-[0.25em]" data-testid="live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
              </span>
              <span className="text-white/60 text-xs font-semibold flex items-center gap-1.5">
                <RefreshCw size={12} /> Auto-refreshes every 8s {refreshedAt && `• updated ${refreshedAt.toLocaleTimeString()}`}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
              className="font-display text-white text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]"
              data-testid="live-matchup"
            >
              {match.team_a} <span className="text-league-red">vs</span> {match.team_b}
            </motion.h1>

            {match.stream_url && toEmbed(match.stream_url) && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
                className="mt-10 max-w-4xl"
                data-testid="live-stream-embed"
              >
                <div className="aspect-video rounded-2xl overflow-hidden border border-white/20 bg-black shadow-2xl">
                  <iframe
                    src={toEmbed(match.stream_url)}
                    title="Live match stream"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </motion.div>
            )}

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
              {(match.innings || []).map((inn, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.2 + i * 0.1, ease: EASE }}
                  className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6 sm:p-8"
                  data-testid={`live-innings-${i}`}
                >
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">{inn.label}</p>
                  <p className="font-oswald text-white text-6xl sm:text-7xl font-semibold mt-3 leading-none" data-testid={`live-score-${i}`}>
                    {inn.runs}<span className="text-white/50 text-4xl">/{inn.wickets}</span>
                  </p>
                  <p className="text-white/70 text-sm font-semibold mt-2">Overs {inn.overs}</p>
                </motion.div>
              ))}
            </div>

            {match.status_text && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-8 inline-block px-6 py-3 rounded-full bg-league-blue border border-white/20 text-white font-oswald font-semibold text-lg"
                data-testid="live-status-text"
              >
                {match.status_text}
              </motion.div>
            )}

            <WinMeter match={match} />

            {(match.balls || []).length > 0 && (
              <div className="mt-10" data-testid="live-balls">
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/50 mb-3">Recent balls</p>
                <div className="flex flex-wrap gap-2">
                  {[...match.balls].reverse().map((b, i) => (
                    <span key={i} className={`w-10 h-10 rounded-full flex items-center justify-center font-oswald font-semibold text-sm ${ballClasses(b)}`}>
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="live-idle">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex items-center gap-3 mb-8">
              <span className="inline-flex w-12 h-12 rounded-full bg-league-red text-white items-center justify-center">
                <Radio size={22} />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/80">Live Matches</span>
            </motion.div>

            <h1 className="font-display text-white leading-[0.9] tracking-tighter" data-testid="live-idle-headline">
              <span className="block overflow-hidden">
                <motion.span className="block text-6xl sm:text-7xl lg:text-[8rem]" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}>
                  {hasMatch ? "THAT'S" : "EVERY BALL."}
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span className="block text-6xl sm:text-7xl lg:text-[8rem] text-outline-white" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.35, ease: EASE }}>
                  {hasMatch ? "FULL TIME." : "LIVE."}
                </motion.span>
              </span>
            </h1>

            {hasMatch && match.status_text && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.55 }}
                className="mt-8 max-w-xl backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6"
                data-testid="live-last-result"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-league-red mb-2">Last result</p>
                <p className="font-oswald text-white text-xl font-semibold">{match.team_a} vs {match.team_b}</p>
                <p className="text-white/70 mt-1">{match.status_text}</p>
              </motion.div>
            )}

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }} className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
              Ball-by-ball live streams of every All Star League 6.0 fixture will play right here. Floodlights on, stream on — December 2026.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }} className="mt-10">
              <NotifyCard source="live" />
            </motion.div>
          </div>
        )}
      </div>
    </main>
  );
}
